<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="isOpen" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" @click.self="close">
        <div
          class="relative w-full rounded-3xl overflow-hidden shadow-lg aspect-[2.7] min-h-100 h-[55vh]  lg:h-auto lg:aspect-[2.7] xl:aspect-[3.3]"
          :style="backgroundStyle"
        >
          <!-- Language Switcher -->
          <div v-if="availableLanguages.length > 0" class="absolute top-4 right-4 z-10 flex gap-2 bg-black/30 backdrop-blur-sm rounded-lg p-2">
            <button
              :class="[
                'px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer',
                selectedLanguage === 'en' ? 'bg-white text-black' : 'text-white hover:bg-white/20'
              ]"
              @click.stop="selectedLanguage = 'en'"
            >
              EN (Original)
            </button>
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              :class="[
                'px-3 py-1 text-xs font-medium rounded transition-colors cursor-pointer',
                selectedLanguage === lang.code ? 'bg-white text-black' : 'text-white hover:bg-white/20'
              ]"
              @click.stop="selectedLanguage = lang.code"
            >
              {{ lang.code.toUpperCase() }}
            </button>
          </div>

          <div class="hidden lg:block absolute left-0 inset-y-0 aspect-[2.6/2] lg:w-100.75 2xl:w-112.5 3xl:w-lg">
            <div class="absolute inset-0 flex flex-col justify-between items-start gap-y-4 xl:gap-y-6 p-6 xl:p-11 text-left">
              <div class="space-y-4 xl:space-y-6">
                <div
                  class="responsive-font-size text-white font-semibold leading-10.5" 
                  :style="{ color: item.textColor, fontSize: titleFontSize }"
                >
                  {{ displayedTitle }}
                </div>
                <div
                  class="text-neutral-400 text-sm font-medium leading-tight content-scroll-desktop whitespace-pre-line" 
                  :style="{ color: item.textColor }"
                >
                  {{ displayedContent }}
                </div>
              </div>
              <div>
                <button
                  class="h-[50px] rounded-full px-7 bg-[#0074e4] hover:opacity-40 border-none text-sm font-semibold text-white clickTarget cursor-pointer"
                  @click.stop="openLink"
                >
                  <span :style="{ color: item.textColor }">{{ displayedActionButtonName }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Right block image - visible only on lg and above -->
          <div
            v-if="item.rightBlockImage"
            class="hidden lg:block absolute right-10 top-10 bottom-6 w-1/3"
          >
            <div
              class="bg-no-repeat bg-contain bg-right w-full h-full"
              :style="{ backgroundImage: `url(${item.rightBlockImage})` }"
            />
          </div>

          <!-- Mobile version -->
          <div class="lg:hidden xs:flex-col xs:gap-y-6 phablet:gap-y-0 phablet:flex-row absolute inset-x-5 bottom-1 flex gap-x-4 xs:px-0 xs:pb-4 phablet:p-5 text-left rounded-3xl bg-gradient-to-b from-[rgba(3,14,33,0.5)] to-[rgba(3,14,33,0.6)] backdrop-blur-[2px]">
            <div class="space-y-4 flex-1">
              <div class="text-white font-semibold text-[32px] leading-[42px] xs:text-center phablet:text-left" :style="{ color: item.textColor }">
                {{ displayedTitle }}
              </div>
              <div class="text-neutral-400 text-sm font-medium leading-tight content-scroll-mobile whitespace-pre-line" :style="{ color: item.textColor }">
                {{ displayedContent }}
              </div>
            </div>
            <div class="shrink-0 flex items-end xs:justify-center phablet:justify-start">
              <button
                class="xs:w-full phablet:w-auto px-5 h-[50px] xs:px-7 xs:h-[52px] phablet:hover:opacity-40
                  border-none text-sm font-semibold bg-[#0074e4] text-white cursor-pointer rounded-full"
                @click.stop="openLink"
              >
                <span :style="{ color: item.textColor }">{{ displayedActionButtonName }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  modelValue: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([ 'update:modelValue' ]);

const isOpen = ref(props.modelValue);
const selectedLanguage = ref('en');

const languageNames = {
  es: 'Spanish',
  sk: 'Slovak',
  ro: 'Romanian',
  pl: 'Polish',
  hu: 'Hungarian',
  de: 'German',
  cz: 'Czech'
};

const availableLanguages = computed(() => {
  if (!props.item.translations || typeof props.item.translations !== 'object') {
    return [];
  }
  return Object.keys(props.item.translations)
    .filter(code => props.item.translations[code] && Object.keys(props.item.translations[code]).length > 0)
    .map(code => ({
      code,
      name: languageNames[code] || code.toUpperCase()
    }));
});

const displayedTitle = computed(() => {
  if (selectedLanguage.value === 'en') {
    return props.item.title;
  }
  return props.item.translations?.[selectedLanguage.value]?.title || props.item.title;
});

const displayedContent = computed(() => {
  if (selectedLanguage.value === 'en') {
    return props.item.content;
  }
  return props.item.translations?.[selectedLanguage.value]?.content || props.item.content;
});

const displayedActionButtonName = computed(() => {
  if (selectedLanguage.value === 'en') {
    return props.item.actionButtonName;
  }
  return props.item.translations?.[selectedLanguage.value]?.actionButtonName || props.item.actionButtonName;
});

const titleFontSize = computed(() => {
  const title = displayedTitle.value || '';
  const length = title.length;

  let baseSize = 32;
  let xlSize = 48;

  if (length > 100) {
    baseSize = 18;
    xlSize = 24;
  } else if (length > 80) {
    baseSize = 20;
    xlSize = 28;
  } else if (length > 60) {
    baseSize = 22;
    xlSize = 32;
  } else if (length > 40) {
    baseSize = 26;
    xlSize = 36;
  } else if (length > 20) {
    baseSize = 30;
    xlSize = 42;
  }

  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--xl-font-size', `${xlSize}px`);
  }

  return `${baseSize}px`;
});

const backgroundStyle = computed(() => {
  const style = {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  if (props.item.backgroundImage) {
    style.backgroundImage = `url(${props.item.backgroundImage})`;
  } else if (props.item.backgroundColor?.includes('gradient')) {
    style.backgroundImage = props.item.backgroundColor;
  } else if (props.item.backgroundColor) {
    style.backgroundColor = props.item.backgroundColor;
  }

  return style;
});

watch(() => props.modelValue, val => {
  isOpen.value = val;
  if (val) {
    selectedLanguage.value = 'en';
  }
});

function close() {
  emit('update:modelValue', false);
}

function openLink() {
  if (props.item.actionType === 'paywall') {
    // TODO: Open paywall modal
    console.log('Opening paywall modal');
    close();
  } else if (props.item.link) {
    window.open(props.item.link, '_blank');
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.responsive-font-size {
  transition: font-size 0.3s ease;
}

@media (min-width: 1280px) {
  .responsive-font-size {
    font-size: var(--xl-font-size, 48px) !important;
  }
}

@media (min-width: 1150px) and (max-width: 1179px) {
  .responsive-font-size {
    font-size: calc(var(--xl-font-size, 48px) * 0.85) !important;
  }
}

@media (min-width: 1280px) and (max-width: 1320px) {
  .responsive-font-size {
    font-size: calc(var(--xl-font-size, 48px) * 0.9) !important;
  }
}

@media (min-width: 1360px) and (max-width: 1390px) {
  .responsive-font-size {
    font-size: calc(var(--xl-font-size, 48px) * 0.88) !important;
  }
}

@media (min-width: 1380px) and (max-width: 1420px) {
  .responsive-font-size {
    font-size: calc(var(--xl-font-size, 48px) * 0.92) !important;
  }
}

@media (min-width: 1550px) and (max-width: 1590px) {
  .responsive-font-size {
    font-size: calc(var(--xl-font-size, 48px) * 0.95) !important;
  }
}

.content-scroll-desktop {
  max-height: 8em;
  overflow-y: auto;
  padding-right: 10px;
}

.content-scroll-mobile {
  max-height: 8em;
  overflow-y: auto;
  padding-right: 10px;
}

.content-scroll-desktop::-webkit-scrollbar {
  width: 7px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
}

.content-scroll-desktop::-webkit-scrollbar-track {
  background: transparent;
}

.content-scroll-desktop::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  min-height: 20px;
}

.content-scroll-desktop::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

.content-scroll-mobile::-webkit-scrollbar {
  width: 7px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
}

.content-scroll-mobile::-webkit-scrollbar-track {
  background: transparent;
}

.content-scroll-mobile::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  min-height: 20px;
}

.content-scroll-mobile::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.5);
}

@media (min-width: 1024px) {
  .content-scroll-desktop {
    max-height: 11em;
  }
}

@media (min-width: 1150px) {
  .content-scroll-desktop {
    max-height: 10em;
  }
}

@media (min-width: 1280px) {
  .content-scroll-desktop {
    max-height: 11em;
  }
}

@media (min-width: 1366px) {
  .content-scroll-desktop {
    max-height: 5em;
  }
}

@media (min-width: 1536px) {
  .content-scroll-desktop {
    max-height: 7em;
  }
}

@media (min-width: 1695px) {
  .content-scroll-desktop {
    max-height: 11em;
  }
}

@media (min-width: 2000px) {
  .content-scroll-desktop {
    max-height: 20em;
  }
}

@media (min-width: 2400px) {
  .content-scroll-desktop {
    max-height: 30em;
  }
}

@media (min-width: 2700px) {
  .content-scroll-desktop {
    max-height: 35em;
  }
}
</style>
